import { useLayoutEffect, useRef, useState } from 'react'
import { CircleCheck, UploadCloud, LoaderCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '../../components/ui/button'
import { uploadKycDocument } from '../../services/kyc'

export interface KycStatusData {
  status?: string
  type?: string
  submittedAt?: number
  reviewedAt?: number
  rejectReason?: string
}

export interface FileUrls {
  legalPersonIdFrontUrl?: string
  legalPersonIdBackUrl?: string
  certificateOfIncorporationUrl?: string
  businessRegistrationUrl?: string
  shareholderStructureUrl?: string
  narOrNcc1Url?: string
  personalDocumentFrontUrl?: string
  personalDocumentBackUrl?: string
  personalHeldIdPhotoUrl?: string
}

interface FileUploadItemProps {
  label: string
  required?: boolean
  fieldKey: keyof FileUrls
  onUploaded: (key: keyof FileUrls, url: string) => void
  uploaded?: string
}

export function FileUploadItem({
  label,
  required = true,
  fieldKey,
  onUploaded,
  uploaded,
}: FileUploadItemProps) {
  const [uploading, setUploading] = useState(false)
  const [name, setName] = useState('')
  const active = useRef(true)
  const sequence = useRef(0)

  useLayoutEffect(() => {
    active.current = true
    return () => {
      active.current = false
      sequence.current += 1
    }
  }, [])

  const upload = async (file?: File) => {
    if (!file || uploading) return
    if (
      !/\.(jpe?g|png|pdf)$/i.test(file.name) ||
      file.size > 10 * 1024 * 1024
    ) {
      toast.error('請選擇不超過 10MB 的 JPG、PNG 或 PDF 檔案')
      return
    }

    const current = ++sequence.current
    setUploading(true)
    try {
      const response = await uploadKycDocument(file)
      if (!active.current || current !== sequence.current) return
      onUploaded(fieldKey, response.data.url)
      setName(file.name)
      toast.success(`${label} 上傳成功`)
    } catch (error) {
      if (active.current && current === sequence.current) {
        toast.error(
          `${label} 上傳失敗：${error instanceof Error ? error.message : '請稍後重試'}`,
        )
      }
    } finally {
      if (active.current && current === sequence.current) setUploading(false)
    }
  }

  return (
    <div className="mb-4">
      <p className="mb-2 text-sm font-medium">
        {required && <span className="mr-1 text-red-600">*</span>}
        {label}
      </p>
      {uploaded ? (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
          <CircleCheck className="size-4 text-green-600" />
          <span className="flex-1">{name || '已上傳'}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              sequence.current += 1
              setName('')
              onUploaded(fieldKey, '')
            }}
          >
            重新上傳
          </Button>
        </div>
      ) : (
        <label
          className="relative flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed border-violet-200 bg-violet-50/40 p-5 text-center text-sm"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault()
            void upload(event.dataTransfer.files[0])
          }}
        >
          <input
            aria-label={label}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            disabled={uploading}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            onChange={(event) => {
              void upload(event.target.files?.[0])
              event.target.value = ''
            }}
          />
          {uploading ? (
            <LoaderCircle className="size-7 animate-spin text-primary" />
          ) : (
            <UploadCloud className="size-7 text-primary" />
          )}
          <span>{uploading ? '上傳中...' : '點擊或拖曳檔案到此區域上傳'}</span>
          <span className="text-xs text-muted-foreground">
            支援 JPG、PNG、PDF，單檔案不超過 10MB
          </span>
        </label>
      )}
    </div>
  )
}
